import { sortBy, uniqBy } from "lodash";
import React, { ReactNode } from "react";
import { Path } from "react-hook-form";
import sanitizeHtml from "sanitize-html";

import { MaterialOption } from "../components/forms/forms-fields/materials-form-fields";
// Do not directly import from "../components/strip/product-finder" (circular imports)
import { defaultProductFinderFilters } from "../components/strips/product-finder/constants";
import { ProductFinderFiltersFormTypes } from "../components/strips/product-finder/types";
import { defaultLocale } from "../i18n/config";
import localesFallbackMap from "../i18n/locales-fallback-map.json";
import { TechSheetFieldType } from "../types/products";
import { getSanitizedHtmlFromAllTags } from "./get-sanitized-html-from-all-tags";
import { getSearchParams, getUpdatedSearchParamsUrl } from "./search-params";
import { slugify } from "./slugify";

export function getTechSheetFieldsListFromSpecializations(
  specializations: Queries.MachineSpecializationDataFragment[]
) {
  return uniqBy(
    specializations.flatMap((specialization) => specialization.techSheet),
    "internalName"
  ).filter((field) => field?.internalName);
}

export class ProductTechSheetRenderer {
  constructor(
    public product: Queries.ProductDataFragment,
    public labels: Record<string, string>
  ) {}

  getAttributes() {
    const { techSheet } = this.product;
    const jsonObj = JSON.parse(techSheet?.internal.content ?? "{}");
    const attributes = jsonObj.attributes ?? {};
    return attributes;
  }

  renderFieldLabel(key: string) {
    const fieldId = this.getAttributes()[key]?.fieldId;
    if (!fieldId) return null;
    return this.product.specialization?.techSheet?.find((techSheetField) => {
      return techSheetField?.internalName === fieldId;
    })?.label;
  }

  renderLocalizedFieldWithFallback(
    translations: Record<string, string | null>,
    lang: string
  ): string | null {
    const translatedValue = translations?.[lang] ?? null;
    if (translatedValue !== null) return translatedValue;

    // a fallback language is available: check if any value is set for that language
    const fallbackLang = (localesFallbackMap as Record<string, string | null>)[lang];
    if (fallbackLang) return this.renderLocalizedFieldWithFallback(translations, fallbackLang);

    // no value and no fallback language available
    return null;
  }

  getFieldValue(key: string): string | null {
    const attribute = this.getAttributes()[key];
    const { node_locale: lang } = this.product;

    let stringValue: string | null | undefined;
    switch (attribute?.valueType) {
      case TechSheetFieldType.SINGLE_SELECT:
        stringValue = this.labels[attribute.value[defaultLocale]] ?? attribute.value[defaultLocale];
        break;
      case TechSheetFieldType.MULTI_SELECT:
        stringValue = sortBy(
          (attribute.value[defaultLocale] as string[])?.map(
            (v) => this.labels[v]?.toLowerCase() ?? v?.toLowerCase()
          )
        ).join(", ");
        break;
      case TechSheetFieldType.TEXT:
      default:
        stringValue = this.renderLocalizedFieldWithFallback(attribute?.value, lang);
    }

    return !stringValue
      ? null
      : sanitizeHtml(stringValue, {
          allowedTags: ["strong", "br"],
        });
  }

  getFlatFieldValue(key: string): string | null {
    const htmlValue = this.getFieldValue(key);
    return getSanitizedHtmlFromAllTags(htmlValue);
  }

  renderFieldValueNode(key: string): ReactNode | null {
    const htmlValue = this.getFieldValue(key);
    return !htmlValue ? null : <p dangerouslySetInnerHTML={{ __html: htmlValue }} />;
  }
}

export function getTechSheetFieldSlugifiedName(internalName: string) {
  const name: Path<ProductFinderFiltersFormTypes> = `techSheetFieldsSlugs.${slugify(
    internalName ?? ""
  )}`;
  return name;
}

export function areFiltersEmpty(filters: Partial<ProductFinderFiltersFormTypes>) {
  const { machiningTypeId, materials, techSheetFieldsSlugs } = filters;
  const filtersAreEmpty =
    (!materials || !Object.values(materials).some(Boolean)) &&
    (!techSheetFieldsSlugs || !Object.values(techSheetFieldsSlugs).some((f) => f.length)) &&
    !machiningTypeId;
  return filtersAreEmpty;
}

/**
 * Please be aware this function uses early returns in order to optimize performances (avoiding
 * unnecessary calculations) and to make it easier to read and to debug it
 * @param products
 * @param appliedFilters
 * @returns The list of products matching filters
 */
export function filterProducts(
  products: Queries.ProductDataFragment[],
  appliedFilters: ProductFinderFiltersFormTypes,
  labels: Record<string, string>
) {
  const { machiningTypeId, materials, techSheetFieldsSlugs } = appliedFilters;
  const hasAnyTechSheetFilters = Object.values(techSheetFieldsSlugs).some((f) => f.length);

  // this way we avoid useless calculations if filters are "logically" empty
  if (areFiltersEmpty(appliedFilters)) return products;

  const filteredProducts = products.filter((product) => {
    // if some material is being filtered
    if (Object.values(materials).some(Boolean)) {
      // Check whether material matches filters. If they mismatch, return false.
      const productMaterial = product.specialization?.machiningType?.material as MaterialOption;
      const materialsMatch = productMaterial && materials[productMaterial];
      if (!materialsMatch) return false;
    }

    // If some machining type is being filtered
    if (machiningTypeId) {
      // Check whether machining type matches filters. If they mismatch, return false.
      const machiningTypeIdMatch = product.specialization?.machiningType?.id === machiningTypeId;
      if (!machiningTypeIdMatch) return false;
    }

    // Begin comparing tech sheet fields.
    //  We want to compare only those fields that belong to the product's specialization
    const specializationFields = product.specialization?.techSheet?.map((field) =>
      slugify(field?.internalName ?? "")
    );

    // we want to filter only the techSheetFields that are not empty and belong to the specialization
    const filteredTechSheetFields = Object.keys(techSheetFieldsSlugs).filter(
      (fieldSlug) =>
        techSheetFieldsSlugs[fieldSlug].length && specializationFields?.includes(fieldSlug)
    );

    // if no tech sheet field was filtered for this product...
    if (filteredTechSheetFields.length === 0) {
      // then we want to check if any tech sheet field at all was filtered (from any specializations)
      // if so, the current product must be excluded (the user is looking for other specific fields)
      // if not, then the user is looking for something else (materials, machining types) so we must
      // include this product.
      return !hasAnyTechSheetFilters;
    }

    // Check whether tech sheet fields match given filters
    const techSheetFieldsMatch = filteredTechSheetFields.every((fieldSlug) => {
      const field = product.specialization?.techSheet?.find(
        (f) => slugify(f?.internalName ?? "") === fieldSlug
      );
      if (!field?.internalName) return false;

      const filterValue = techSheetFieldsSlugs[fieldSlug];

      const productTechSheetRenderer = new ProductTechSheetRenderer(product, labels);
      const attributes = productTechSheetRenderer.getAttributes();

      // this is the localized value used in text values
      // this removes <br/> the editor might have inserted but the user doesnt know exists
      const flatFieldValue = productTechSheetRenderer.getFlatFieldValue(field.internalName);
      // This is the "internalName" that is used as a reference in multi and single selects
      const rawFieldValue = attributes?.[field.internalName]?.value?.[defaultLocale];

      switch (field.valueType) {
        case TechSheetFieldType.TEXT:
          return !filterValue || flatFieldValue?.match(new RegExp(filterValue as string, "g"));
        case TechSheetFieldType.SINGLE_SELECT:
          return !filterValue || rawFieldValue === filterValue;
        case TechSheetFieldType.MULTI_SELECT:
          // Empty filter value, can be skipped
          if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
            return true;
          }
          return (
            Array.isArray(rawFieldValue) &&
            filterValue.every((singleValue) => (rawFieldValue as string[]).includes(singleValue))
          );
        default:
          return false; // Should never happen
      }
    });

    return techSheetFieldsMatch;
  });

  return filteredProducts;
}

export function getDefaultProductFinderFilters(
  specializations: Queries.MachineSpecializationDataFragment[]
) {
  const defaultFiltersValues: ProductFinderFiltersFormTypes = {
    ...defaultProductFinderFilters,
    techSheetFieldsSlugs: {
      ...defaultProductFinderFilters.techSheetFieldsSlugs,
      ...specializations.reduce(
        (acc, cur) => {
          cur.techSheet?.forEach((field) => {
            if (field?.internalName) {
              acc[slugify(field?.internalName)] =
                field.valueType === TechSheetFieldType.MULTI_SELECT ? [] : "";
            }
          });
          return acc;
        },
        {} as Record<string, string | string[]>
      ),
    },
  };

  if (specializations.length === 1) {
    const [specialization] = specializations;
    defaultFiltersValues.machiningTypeId = specialization.machiningType?.id ?? "";
    const material = specialization.machiningType?.material as MaterialOption;
    if (material) {
      defaultFiltersValues.materials[material] = true;
    }
  }
  return defaultFiltersValues;
}

export function getFiltersFromSearchParams(
  searchParamKey: string
): null | Partial<ProductFinderFiltersFormTypes> {
  const searchParams = getSearchParams();
  const searchParamFiltersAsString = searchParams?.get(searchParamKey || "filter");
  if (!searchParamFiltersAsString) return null;
  return JSON.parse(searchParamFiltersAsString);
}

export function setFiltersInSearchParams(
  _filter: null | Partial<ProductFinderFiltersFormTypes>,
  specializations: Queries.MachineSpecializationDataFragment[],
  searchParamKey: string
) {
  const isSingleSpecialization = specializations.length === 1;
  const filter = { ..._filter };
  if (isSingleSpecialization) {
    delete filter.machiningTypeId;
    delete filter.materials;
  }
  if (filter.techSheetFieldsSlugs) {
    Object.keys(filter.techSheetFieldsSlugs).forEach((key) => {
      if (!filter.techSheetFieldsSlugs?.[key]?.length) {
        delete filter.techSheetFieldsSlugs?.[key];
      }
    });
    if (Object.keys(filter.techSheetFieldsSlugs).length === 0) {
      delete filter.techSheetFieldsSlugs;
    }
  }
  if (typeof window !== "undefined") {
    const path = getUpdatedSearchParamsUrl({
      [searchParamKey || "filter"]:
        filter && Object.keys(filter).length ? JSON.stringify(filter) : null,
    });
    window.history.pushState({ path }, "", path);
  }
}
